// Source-of-truth for the blog index. Each post tracks the URL of the
// post detail page on disk so adding a new post means dropping it in
// blog/<slug>/index.html, then prepending an entry here.
//
// Title, dek, author, and date are kept in sync with the actual post
// pages — if you edit a post's hero copy, update the matching entry here.
import gettingStartedCover from "../../assets/getting-started-hero.png";
import lifeIsBetterCoverMobile from "../../assets/life-is-better-hero-mobile.svg";
import lifeIsBetterCover from "../../assets/life-is-better-hero.svg";
import type { MascotName } from "../../components/Mascot";

export type BlogTint = "indigo" | "sage" | "plum" | "amber";

export type BlogAuthor = {
  name: string;
  role?: string;
  /** Optional author image (relative to public/ or imported asset URL). */
  imgSrc?: string;
  /**
   * Optional brand-style avatar in place of a portrait. "scribenote" renders
   * the purple SN gradient puck used on the Getting Started post hero.
   */
  logo?: "scribenote";
};

export type BlogPost = {
  id: string;
  title: string;
  dek: string;
  tag: string;
  tint: BlogTint;
  /** ISO date — used for sort + the date column in the list. */
  date: string;
  dateLabel: string;
  readTime?: string;
  author: BlogAuthor;
  mascot?: MascotName;
  /** Optional cover image. Falls back to the striped placeholder + mascot peek. */
  coverImageSrc?: string;
  /**
   * Optional cover image for narrow viewports (≤ 640px). Useful when the
   * desktop image is wide and crops poorly on phones.
   */
  coverImageSrcMobile?: string;
  /** Object-position for the cover image (e.g. "35% 50%" to center on a subject). */
  coverImagePosition?: string;
  /**
   * Override the cover treatment. "companions" renders the four-Companion
   * lineup used on the "Life is better with Companions" hero.
   */
  coverArt?: "companions";
  /** URL of the post detail page. `null` = "coming soon" placeholder. */
  href: string | null;
  featured?: boolean;
};

// Newest first. The first `featured: true` entry is the big editorial card;
// the rest fall into the archive list below.
export const POSTS: BlogPost[] = [
  {
    id: "getting-started",
    title: "Getting Started with Companion.",
    dek: "Your AI assistant inside Scribenote. It amplifies your ability as a veterinary professional — handling documentation, research, client communication, and more — so you can stay focused on the patient in front of you.",
    tag: "Product",
    tint: "sage",
    date: "2026-04-30",
    dateLabel: "April 2026",
    author: { name: "The Scribenote Team", logo: "scribenote" },
    mascot: "Otto",
    coverImageSrc: gettingStartedCover,
    coverImagePosition: "35% 50%",
    href: "/blog/getting-started-with-companion/",
    featured: true,
  },
  {
    id: "life-is-better",
    title: "Life is better with Companions.",
    dek: "This May, the team that brought AI scribing to veterinary medicine will launch an advanced new veterinary artificial intelligence. A digital brain for your clinic.",
    tag: "Essay",
    tint: "indigo",
    date: "2026-04-23",
    dateLabel: "April 2026",
    author: {
      name: "Ryan Gallagher",
      role: "Co-founder, Scribenote",
      imgSrc: "/blog/authors/ryan.png",
    },
    mascot: "Quill",
    coverImageSrc: lifeIsBetterCover,
    coverImageSrcMobile: lifeIsBetterCoverMobile,
    coverImagePosition: "center",
    href: "/blog/life-is-better-with-companions/",
  },
];
